import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { UploadSimple, X, CircleNotch, CheckCircle, Warning } from "@phosphor-icons/react";
import { uploadPhoto, submitLead } from "@/lib/api";

const SERVICE_TYPES = [
  { v: "service", l: "Service" },
  { v: "repair", l: "Repair" },
  { v: "installation", l: "Installation" },
  { v: "replacement", l: "Replacement" },
  { v: "rtu", l: "RTU Change-Out" },
  { v: "other", l: "Other" },
];

const CONTACT_METHODS = [
  { v: "phone", l: "Phone" },
  { v: "email", l: "Email" },
  { v: "text", l: "Text" },
];

const emptyForm = {
  name: "", phone: "", email: "", property_address: "",
  customer_type: "residential", service_type: "service",
  equipment_type: "", description: "", preferred_contact: "phone",
};

export const ServiceForm = ({ sourcePage = "home", commercial = false }) => {
  const [form, setForm] = useState({ ...emptyForm, customer_type: commercial ? "commercial" : "residential" });
  const [emergency, setEmergency] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const focusCls = emergency ? "focus:border-brand-ember focus:ring-brand-ember" : "focus:border-brand-cyan focus:ring-brand-cyan";
  const activeCls = emergency ? "border-brand-ember bg-brand-ember/10 text-brand-ember" : "border-brand-cyan bg-brand-cyan/10 text-brand-cyan";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - photos.length);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const ref = await uploadPhoto(file);
        uploaded.push(ref);
      }
      setPhotos((p) => [...p, ...uploaded]);
      toast.success(`${uploaded.length} photo${uploaded.length > 1 ? "s" : ""} attached`);
    } catch {
      toast.error("Photo upload failed. Please try a smaller image.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = (id) => setPhotos((p) => p.filter((x) => x.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Name, phone, and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({
        ...form,
        photos,
        source_page: sourcePage,
        description: emergency ? `[EMERGENCY / NO-HEAT] ${form.description}` : form.description,
      });
      setDone(true);
      toast.success("Request received. We'll be in touch shortly.");
    } catch {
      toast.error("Something went wrong. Please call 773-931-7012.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="form-success" className="border border-brand-cyan/30 bg-panel p-10 text-center panel-glow">
        <CheckCircle size={56} weight="duotone" className="mx-auto text-brand-cyan" />
        <h3 className="mt-5 font-display text-2xl font-bold">Request Received</h3>
        <p className="mt-3 font-body text-sm text-slatex-300">
          Thank you. A member of the Genuine Forge team will review your request and reach out via your preferred contact method.
        </p>
        <button data-testid="form-reset" onClick={() => { setForm(emptyForm); setPhotos([]); setDone(false); setEmergency(false); }}
          className="mt-6 border border-white/15 px-6 py-2.5 font-mono text-xs uppercase tracking-widest text-white transition-colors hover:border-white/40">
          Submit another request
        </button>
      </div>
    );
  }

  const inputCls = `w-full border border-white/12 bg-panel px-4 py-3 font-body text-sm text-white placeholder:text-slatex-500 outline-none transition-colors focus:ring-1 ${focusCls}`;

  return (
    <form data-testid="service-form" onSubmit={submit} className={`border ${emergency ? "border-brand-ember/40" : "border-white/10"} bg-panel/60 p-6 backdrop-blur-sm transition-colors md:p-8`}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className={`h-2 w-2 ${emergency ? "bg-brand-ember pulse-ring" : "bg-brand-cyan"}`} />
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-slatex-300">{commercial ? "Project Review Request" : "Service Request"}</span>
        </div>
        <button type="button" data-testid="emergency-toggle" onClick={() => setEmergency((v) => !v)}
          className={`flex items-center gap-2 border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${emergency ? "border-brand-ember bg-brand-ember/15 text-brand-ember" : "border-white/15 text-slatex-300 hover:border-white/40"}`}>
          <Warning size={13} weight="fill" /> No Heat / Emergency
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <input data-testid="form-name" className={inputCls} placeholder="Name *" value={form.name} onChange={set("name")} />
        <input data-testid="form-phone" className={inputCls} placeholder="Phone *" value={form.phone} onChange={set("phone")} />
        <input data-testid="form-email" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={set("email")} />
        <input data-testid="form-address" className={inputCls} placeholder="Property Address" value={form.property_address} onChange={set("property_address")} />
      </div>

      <div className="mt-5">
        <span className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slatex-500">Property Type</span>
        <div className="flex gap-2">
          {["residential", "commercial"].map((t) => (
            <button key={t} type="button" data-testid={`type-${t}`} onClick={() => setForm((f) => ({ ...f, customer_type: t }))}
              className={`flex-1 border px-4 py-2.5 font-mono text-xs uppercase tracking-widest transition-colors ${form.customer_type === t ? activeCls : "border-white/12 text-slatex-300 hover:border-white/30"}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <span className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slatex-500">Service Type</span>
          <select data-testid="form-service-type" className={inputCls} value={form.service_type} onChange={set("service_type")}>
            {SERVICE_TYPES.map((s) => <option key={s.v} value={s.v} className="bg-panel">{s.l}</option>)}
          </select>
        </div>
        <div>
          <span className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slatex-500">Equipment Type</span>
          <input data-testid="form-equipment" className={inputCls} placeholder="Furnace, condenser, RTU…" value={form.equipment_type} onChange={set("equipment_type")} />
        </div>
      </div>

      <div className="mt-5">
        <span className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slatex-500">Brief Description</span>
        <textarea data-testid="form-description" rows={3} className={inputCls} placeholder="Describe the issue or project…" value={form.description} onChange={set("description")} />
      </div>

      <div className="mt-5">
        <span className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slatex-500">Preferred Contact Method</span>
        <div className="flex gap-2">
          {CONTACT_METHODS.map((m) => (
            <button key={m.v} type="button" data-testid={`contact-${m.v}`} onClick={() => setForm((f) => ({ ...f, preferred_contact: m.v }))}
              className={`flex-1 border px-3 py-2 font-mono text-xs uppercase tracking-widest transition-colors ${form.preferred_contact === m.v ? activeCls : "border-white/12 text-slatex-300 hover:border-white/30"}`}>
              {m.l}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5">
        <span className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slatex-500">Photos (optional, up to 5)</span>
        <input ref={fileRef} data-testid="form-file-input" type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" />
        <button type="button" data-testid="form-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading || photos.length >= 5}
          className="flex w-full items-center justify-center gap-2 border border-dashed border-white/20 bg-panel/50 py-4 font-mono text-xs uppercase tracking-widest text-slatex-300 transition-colors hover:border-white/40 disabled:opacity-50">
          {uploading ? <CircleNotch size={16} className="animate-spin" /> : <UploadSimple size={16} />}
          {uploading ? "Uploading…" : "Attach Photos"}
        </button>
        {photos.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {photos.map((p) => (
              <div key={p.id} data-testid={`photo-chip-${p.id}`} className="flex items-center gap-2 border border-white/12 bg-panel px-3 py-1.5">
                <span className="max-w-[120px] truncate font-mono text-[11px] text-slatex-300">{p.original_filename}</span>
                <button type="button" onClick={() => removePhoto(p.id)} className="text-slatex-500 hover:text-brand-ember"><X size={12} weight="bold" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <motion.button whileTap={{ scale: 0.98 }} type="submit" data-testid="form-submit" disabled={submitting}
        className={`mt-7 flex w-full items-center justify-center gap-2 border py-4 font-mono text-sm uppercase tracking-widest transition-colors disabled:opacity-60 ${emergency ? "border-brand-ember bg-brand-ember text-white hover:bg-brand-ember/85" : "border-brand-cyan bg-brand-cyan text-ink hover:bg-brand-cyan/85"}`}>
        {submitting && <CircleNotch size={16} className="animate-spin" />}
        {commercial ? "Request Project Review" : "Request Service"}
      </motion.button>
    </form>
  );
};

export default ServiceForm;
